import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { InferRequestType } from "hono/client";
import client from "../../utils/api-client";

// types
type AddressRouteWithId = (typeof client.addresses)[":addressId"];
type CreateAddressInput = InferRequestType<
	(typeof client.addresses.index)["$post"]
>["json"];
type UpdateAddressInput = InferRequestType<
	AddressRouteWithId["$put"]
>["param"] &
	InferRequestType<AddressRouteWithId["$put"]>["json"];
type DeleteAddressInput = InferRequestType<
	AddressRouteWithId["$delete"]
>["param"];
type ValidateAddressInput = InferRequestType<
	(typeof client.addresses)["validate-address"]["$post"]
>["json"];

export type AddressApiRequestTypes = {
	create: CreateAddressInput;
	update: UpdateAddressInput;
	delete: DeleteAddressInput;
	validateAddress: ValidateAddressInput;
};

export type AddressApiResponseTypes = {
	getAll: Awaited<ReturnType<typeof useGetAllAddressesQuery>>["data"];
	getZones: Awaited<ReturnType<typeof useGetZonesQuery>>["data"];
	create: Awaited<ReturnType<typeof useCreateAddressMutation>>["data"];
	update: Awaited<ReturnType<typeof useUpdateAddressMutation>>["data"];
	delete: Awaited<ReturnType<typeof useDeleteAddressMutation>>["data"];
	validateAddress: Awaited<
		ReturnType<typeof useValidateAddressMutation>
	>["data"];
};

// queries
export function useGetAllAddressesQuery() {
	return useQuery({
		queryKey: ["user", "addresses"],
		queryFn: async () => {
			const result = await client.addresses.index.$get();
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
	});
}

export function useGetZonesQuery() {
	return useQuery({
		queryKey: ["locations", "zones"],
		queryFn: async () => {
			const result = await client.addresses.zones.$get();
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
	});
}

// mutations
export function useCreateAddressMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (json: CreateAddressInput) => {
			const result = await client.addresses.index.$post({
				json,
			});
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["user", "addresses"] });
		},
	});
}

export function useUpdateAddressMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (input: UpdateAddressInput) => {
			const { addressId, ...json } = input;
			const result = await client.addresses[":addressId"].$put({
				param: { addressId },
				json,
			});
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["user", "addresses"] });
		},
	});
}

export function useDeleteAddressMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (param: DeleteAddressInput) => {
			const result = await client.addresses[":addressId"].$delete({
				param,
			});
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["user", "addresses"] });
		},
	});
}

export function useValidateAddressMutation() {
	return useMutation({
		mutationFn: async (json: ValidateAddressInput) => {
			const result = await client.addresses["validate-address"].$post({
				json,
			});
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
	});
}
